import React, { useState } from 'react';
import { useMetadata } from '../../context/MetadataContext'; 
import { getValue } from '../../utils/getValue';
import { useGameState } from '../../context/GameStateContext';
import { useGlobalError } from '../../context/GlobalErrorContext';
import LeaderboardModal from '../LeaderboardModal';
import './TournamentPuzzleInfo.css';
import { 
  ArrowLeft,       // Back
  List,            // Leaderboard
  Play             // Start
} from 'lucide-react';

const TournamentPuzzleInfo = () => {
  const { getText } = useMetadata();
  const { gameData, rejectPuzzle, startPuzzle } = useGameState();
  const { triggerError } = useGlobalError();
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);

  if (!gameData?.state) {
    triggerError('high', getText('errors.request_failed'));
    return null;
  }

  const handleBackToFind = () => {
    rejectPuzzle();
  };

  const handleStartPuzzle = () => {
    startPuzzle();
  };

  const handleShowLeaderboard = () => {
    setIsLeaderboardOpen(true);
  };

  const handleCloseLeaderboard = () => {
    setIsLeaderboardOpen(false);
  };
  
  return (
    <div className="dataContainer">
      <div className="containerHeader">
        <h3>{getText('tournament_mode.info_title')}</h3>
      </div>
      
      <div className="dataContent">
        <div className="contentGroup">
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.style')}:</span>
            <span className="dataValue">
              {(() => {
                const v = getValue(gameData, 'puzzle.style');
                if (!v) return 'N/A';
                const displayName = v.charAt(0).toUpperCase() + v.slice(1);
                return (
                  <a
                    href={`https://gridrank.games/guides/${v.toLowerCase()}.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {displayName}
                  </a>
                );
              })()}
            </span>
          </div>
          
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.size')}:</span>
            <span className="dataValue">{getValue(gameData, 'puzzle.size')}</span>
          </div>
          
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.difficulty')}:</span>
            <span className="dataValue">{getValue(gameData, 'puzzle.difficulty')}</span>
          </div>
        </div>
        
        <div className="contentGroup">
          <div className="dataRow">
            <span className="dataLabel">{getText('tournament_mode.progress')}:</span>
            <span className="dataValue">
              {getValue(gameData, 'stats.user_finished_count', 0)} / {getValue(gameData, 'stats.puzzle_count', 0)}
            </span>
          </div>

          
          <div className="progressBarContainer">
            <div 
              className="progressBar" 
              style={{ width: `${getValue(gameData, 'stats.user_finished_percent', 0)}%` }}
            ></div>
          </div>
        </div>
        
        <div className="contentGroup">
          <div className="dataRow">
            <span className="dataLabel">{getText('tournament_mode.participants')}:</span>
            <span className="dataValue">{getValue(gameData, 'stats.player_count', 0)}</span>
          </div>
          
          <div className="dataRow">
            <span className="dataLabel">{getText('tournament_mode.finished_players')}:</span>
            <span className="dataValue">
              {getValue(gameData, 'stats.finished_player_count', 0)} ({getValue(gameData, 'stats.finished_player_percent', 0)}%)
            </span>
          </div>
          
          {getValue(gameData, 'stats.expires_ts') && (
            <div className="dataRow">
              <span className="dataLabel">{getText('tournament_mode.expires')}:</span>
              <span className="dataValue">
                {new Date(getValue(gameData, 'stats.expires_ts') * 1000).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
      </div>
      
      <div className="actionButtonContainer">
        <button 
          className="iconButton secondaryButton"
          onClick={handleBackToFind}
          title={getText('common_text.back')}
          aria-label={getText('common_text.back')}
        >
          <ArrowLeft className="buttonIcon" />
        </button>
        
        <button 
          className="iconButton secondaryButton" 
          onClick={handleShowLeaderboard}
          title={getText('common_text.leaderboard')}
          aria-label={getText('common_text.leaderboard')}
        >
          <List className="buttonIcon" />
        </button>
        
        <button 
          className="iconButton primaryButton" 
          onClick={handleStartPuzzle}
          title={getText('common_text.start')}
          aria-label={getText('common_text.start')}
        >
          <Play className="buttonIcon" />
        </button>
      </div>
      
      <LeaderboardModal 
        isOpen={isLeaderboardOpen}
        onClose={handleCloseLeaderboard}
        tournamentKey={getValue(gameData, 'state.tournament_key')}
      />
    </div>
  );
};

export default TournamentPuzzleInfo;
